/** A link to a bank data aggregator. Today only SimpleFIN Bridge (https://beta-bridge.simplefin.org). */
export type BankProvider = 'simplefin';

/** `revoked`: the access was disabled at the provider, so the user has to connect again. */
export type BankConnectionStatus = 'active' | 'error' | 'revoked';

/** What an account is, which decides how its transactions count. SimpleFIN doesn't say, so it is guessed. */
export type BankAccountKind = 'checking' | 'savings' | 'credit' | 'investment' | 'loan' | 'other';

export const BANK_ACCOUNT_KINDS: BankAccountKind[] = ['checking', 'savings', 'credit', 'investment', 'loan', 'other'];

/**
 * What a bank transaction became: its own expense, the same charge as an expense read from email, money moved
 * between the person's own accounts, a card bill payment, income, or nothing (an account that holds no spending).
 */
export type BankRole = 'expense' | 'matched' | 'transfer' | 'card_payment' | 'income' | 'ignored';

export interface BankAccount {
  id: string;
  connectionId: string;
  institution: string | null;
  name: string;
  currency: string;
  balance: number | null;
  availableBalance: number | null;
  balanceDate: string | null;
  kind: BankAccountKind;
  hidden: boolean;
  /** The transactions held for the account: how many, and the dates they span. */
  transactionCount: number;
  firstTransactionDate: string | null;
  lastTransactionDate: string | null;
  /** How many of its transactions became each role (absent before the first expense rebuild). */
  roles: Partial<Record<BankRole, number>>;
}

export interface BankConnection {
  id: string;
  provider: BankProvider;
  status: BankConnectionStatus;
  /** True while a sync runs, including the first one that walks back through history. */
  syncing: boolean;
  historySynced: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  createdAt: string;
  accounts: BankAccount[];
}
